import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSupabaseData } from '../../contexts/SupabaseDataContext';
import SafeIcon from '../../common/SafeIcon';
import PolicyDocument from '../policies/PolicyDocument';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiCheckCircle, FiChevronRight, FiChevronLeft, FiShield } = FiIcons;

const DEFAULT_POLICY = {
  paymentMode: 'seller_direct',
  returnsAccepted: false,
  returnTimeframe: '24h',
  refundType: 'replacement',
  cancelWindow: '2h',
  changeWindow: '1h',
  qualityPromise: 'made_fresh',
  deliveryPolicy: 'flexible',
  responseTime: '24h'
};

const STEPS = [
  { id: 'quality', title: 'Quality Guarantee' },
  { id: 'returns', title: 'Returns & Refunds' },
  { id: 'fulfillment', title: 'Fulfillment Rules' },
  { id: 'support', title: 'Support & Resolution' }
];

const PolicyBuilderWizard = ({ seller }) => {
  const { updateSellerProfile } = useSupabaseData();
  const [currentStep, setCurrentStep] = useState(0);
  const [policyData, setPolicyData] = useState(DEFAULT_POLICY);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (seller?.policy_settings && Object.keys(seller.policy_settings).length > 0) {
      setPolicyData({ ...DEFAULT_POLICY, ...seller.policy_settings });
    }
  }, [seller]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateSellerProfile(seller.id, { policy_settings: policyData });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Failed to save policies", err);
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field, value) => {
    setPolicyData(prev => ({ ...prev, [field]: value }));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-8">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Quality Guarantee</label>
              <div className="space-y-3">
                {[
                  { id: 'made_fresh', label: 'Made Fresh Promise', desc: 'Emphasize high-quality ingredients and preparation.' },
                  { id: '100_satisfaction', label: '100% Satisfaction', desc: 'Promise to make it right if they are not happy.' }
                ].map(opt => (
                  <label key={opt.id} className={`flex items-start p-4 border rounded-2xl cursor-pointer transition-all ${policyData.qualityPromise === opt.id ? 'border-indigo-600 bg-indigo-50/50' : 'border-slate-100 bg-slate-50 hover:border-indigo-300'}`}>
                    <input
                      type="radio"
                      name="quality"
                      checked={policyData.qualityPromise === opt.id}
                      onChange={() => updateField('qualityPromise', opt.id)}
                      className="mt-1 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div className="ml-3">
                      <span className="block text-sm font-black text-slate-900">{opt.label}</span>
                      <span className="block text-xs text-slate-500 font-medium mt-1">{opt.desc}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-8">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Do you accept returns?</label>
              <div className="flex gap-4">
                <button
                  onClick={() => updateField('returnsAccepted', true)}
                  className={`flex-1 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all border ${policyData.returnsAccepted ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}
                >Yes</button>
                <button
                  onClick={() => updateField('returnsAccepted', false)}
                  className={`flex-1 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all border ${!policyData.returnsAccepted ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}
                >No</button>
              </div>
            </div>

            {policyData.returnsAccepted && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Return Window (Post-Fulfillment)</label>
                <select
                  value={policyData.returnTimeframe}
                  onChange={(e) => updateField('returnTimeframe', e.target.value)}
                  className="w-full px-5 py-4 bg-slate-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 font-bold text-sm outline-none"
                >
                  <option value="2 hours">Within 2 Hours</option>
                  <option value="12 hours">Within 12 Hours</option>
                  <option value="24h">Within 24 Hours</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Refund Policy</label>
              <select
                value={policyData.refundType}
                onChange={(e) => updateField('refundType', e.target.value)}
                className="w-full px-5 py-4 bg-slate-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 font-bold text-sm outline-none"
              >
                <option value="replacement">Replacement Preferred</option>
                <option value="full">Full Refunds Issued</option>
                <option value="partial">Partial Refunds Issued</option>
                <option value="credit">Store Credit Only</option>
              </select>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-8">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Cancellation Window</label>
              <select
                value={policyData.cancelWindow}
                onChange={(e) => updateField('cancelWindow', e.target.value)}
                className="w-full px-5 py-4 bg-slate-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 font-bold text-sm outline-none"
              >
                <option value="1 hour">Within 1 hour of acceptance</option>
                <option value="2 hours">Within 2 hours of acceptance</option>
                <option value="4 hours">Within 4 hours of acceptance</option>
                <option value="12 hours">Within 12 hours of acceptance</option>
                <option value="24 hours">Within 24 hours of acceptance</option>
                <option value="none">No cancellations after acceptance</option>
              </select>
              <p className="mt-2 text-[9px] text-slate-400 font-medium">Determines how long a buyer has to cancel after you accept their order.</p>
            </div>

            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Change Window</label>
              <select
                value={policyData.changeWindow}
                onChange={(e) => updateField('changeWindow', e.target.value)}
                className="w-full px-5 py-4 bg-slate-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 font-bold text-sm outline-none"
              >
                <option value="1 hour">Within 1 hour of acceptance</option>
                <option value="2 hours">Within 2 hours of acceptance</option>
                <option value="4 hours">Within 4 hours of acceptance</option>
                <option value="none">No changes after acceptance</option>
              </select>
              <p className="mt-2 text-[9px] text-slate-400 font-medium">Determines the cutoff for modifying order details once confirmed.</p>
            </div>

            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Fulfillment Flexibility</label>
              <div className="space-y-3">
                {[
                  { id: 'flexible', label: 'Flexible Window', desc: 'Allows customers to reschedule easily.' },
                  { id: 'strict', label: 'Strict Timing', desc: 'Requires punctual arrival for pickup/delivery.' }
                ].map(opt => (
                  <label key={opt.id} className={`flex items-start p-4 border rounded-2xl cursor-pointer transition-all ${policyData.deliveryPolicy === opt.id ? 'border-indigo-600 bg-indigo-50/50' : 'border-slate-100 bg-slate-50 hover:border-indigo-300'}`}>
                    <input
                      type="radio"
                      name="delivery"
                      checked={policyData.deliveryPolicy === opt.id}
                      onChange={() => updateField('deliveryPolicy', opt.id)}
                      className="mt-1 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div className="ml-3">
                      <span className="block text-sm font-black text-slate-900">{opt.label}</span>
                      <span className="block text-xs text-slate-500 font-medium mt-1">{opt.desc}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-8">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Support Response Time</label>
              <select
                value={policyData.responseTime}
                onChange={(e) => updateField('responseTime', e.target.value)}
                className="w-full px-5 py-4 bg-slate-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 font-bold text-sm outline-none"
              >
                <option value="1 hour">Within 1 Hour</option>
                <option value="4 hours">Within 4 Hours</option>
                <option value="12 hours">Within 12 Hours</option>
                <option value="24h">Within 24 Hours</option>
              </select>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-10">
      <div className="lg:w-[45%] flex flex-col">
        <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm flex-1">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-xl font-black text-slate-900 tracking-tight">Policy Wizard</h3>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Guided Store Policies</p>
            </div>
            <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 px-4 py-1.5 rounded-full uppercase tracking-widest">
              {currentStep + 1} / {STEPS.length}
            </span>
          </div>

          <div className="flex mb-10 space-x-2">
            {STEPS.map((step, idx) => (
              <div key={idx} className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${idx <= currentStep ? 'bg-indigo-600' : 'bg-slate-100'}`} />
            ))}
          </div>

          <h4 className="text-sm font-black text-indigo-500 uppercase tracking-[0.2em] mb-8">{STEPS[currentStep].title}</h4>

          <div className="min-h-[380px]">
            {renderStepContent()}
          </div>

          <div className="flex justify-between items-center mt-12 pt-8 border-t border-slate-50">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-900 disabled:opacity-30 transition-colors flex items-center"
            >
              <SafeIcon icon={FiChevronLeft} className="mr-2" /> Back
            </button>

            {currentStep < STEPS.length - 1 ? (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                className="px-10 py-4 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 active:scale-95"
              >
                Continue <SafeIcon icon={FiChevronRight} className="ml-2 inline" />
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-10 py-4 bg-indigo-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 active:scale-95"
              >
                {isSaving ? 'Publishing...' : <><SafeIcon icon={FiSave} className="mr-2 inline" /> Publish Policy</>}
              </button>
            )}
          </div>
        </div>

        <AnimatePresence>
          {saved && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="mt-6 bg-emerald-50 border border-emerald-100 p-5 rounded-2xl flex items-center text-emerald-700 font-bold text-sm shadow-sm"
            >
              <SafeIcon icon={FiCheckCircle} className="mr-3 text-lg" />
              Store policies have been published to your profile!
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="lg:w-[55%]">
        <div className="bg-slate-50/50 rounded-[2.5rem] border border-slate-200 p-8 sticky top-32">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center text-slate-400">
              <SafeIcon icon={FiShield} className="mr-3 text-xl" />
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em]">Real-Time Preview</h3>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Live Updates</span>
            </div>
          </div>
          <div className="max-h-[650px] overflow-y-auto pr-2 no-scrollbar">
            <PolicyDocument settings={policyData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyBuilderWizard;